import { TrashIcon } from '@heroicons/react/24/solid';
import { Snowflake, Trash2 } from 'lucide-react';

import {
  classifyAssetStatus,
  getActionApplicability,
} from '@/components/actions';
import { UnfreezeIcon } from '@/components/icons/Unfreeze.icon';
import { StatusOptionBuilderParams } from '@/sections/asset/types';
import { AssetStatus } from '@/types';
import type { DropdownOption } from '@/types/dropdown.types';

/**
 * Builds status management dropdown options
 * Conditionally shows freeze/unfreeze based on current asset states and user permissions
 * Includes keyboard shortcut hints
 *
 * @param params - Parameters for building status options
 * @returns Array of dropdown options for status management actions
 */
export const getAssetStatusOptions = ({
  assets,
  handlers,
  isPraetorianUser = false,
}: StatusOptionBuilderParams): DropdownOption[] => {
  // Share the entity-agnostic gating util so the row menu, the bulk toolbar,
  // and the drawer all hide the Freeze/Unfreeze no-op by the same rule.
  const { hideFreeze, hideUnfreeze } = getActionApplicability(
    assets.map(a => classifyAssetStatus(a.status))
  );

  return [
    // Show Freeze unless every selected asset is already frozen.
    ...(!hideFreeze
      ? [
          {
            label: 'Freeze Asset',
            icon: <Snowflake className="size-4" />,
            onClick: () =>
              handlers.handleUpdateStatus(assets, AssetStatus.Frozen),
            count: ` ⌥ F`,
          },
        ]
      : []),
    // Show Unfreeze only when at least one selected asset is frozen.
    ...(!hideUnfreeze
      ? [
          {
            label: 'Unfreeze Asset',
            icon: <UnfreezeIcon className="size-4" />,
            onClick: () =>
              handlers.handleUpdateStatus(assets, AssetStatus.Active),
            count: ` ⌥ U`,
          },
        ]
      : []),
    // Always show Delete option
    {
      label: 'Delete Asset',
      icon: <Trash2 className="size-4" />,
      onClick: () => handlers.handleUpdateStatus(assets, AssetStatus.Deleted),
      count: ` ⌥ D`,
    },
    // Praetorian-only permanent (hard) delete — hidden unless a handler is
    // supplied and the current user is a Praetorian user. Set apart from the
    // customer-facing soft "Delete Asset" with a divider, text-destructive,
    // and a distinct solid trash icon (soft delete keeps its outline Trash2).
    ...(isPraetorianUser && handlers.handleHardDelete
      ? [
          { label: '', isDivider: true },
          {
            label: 'Delete Permanently',
            icon: <TrashIcon className="size-4" />,
            className: 'text-destructive',
            destructive: true,
            onClick: () => handlers.handleHardDelete?.(assets),
          },
        ]
      : []),
  ];
};
